import { useCallback, useMemo, useState } from "react";
import { Card, Label, MonoMeta } from "@/design/primitives";
import { cn } from "@/lib/utils";

// ── DeskTasks (Jeff 2026-11-03: the right column is HIS task list) ──────────
// Replaces the +Idea composer on the desk. Tasks are Jeff's own small, specific,
// one-off things to do: a one-line title, up to ~3 lines of description, and
// (optionally) a link to a project row. Click a task to expand it in place —
// editing, check-off, and clear live in the expansion. Rows persist on the
// goals board as kind="task" (kind/state OPEN/DONE), so they survive syncs
// like every other Jeff-owned row. NOT executor work — nothing here is "in
// flight"; it's a checklist Jeff owns and works by hand.

// Links (Jeff 11-03): pasted URLs in a task's title or description render as
// real anchors opening in a new tab. Link click doesn't expand the row.
const URL_RE = /(https?:\/\/[^\s<>"']+)/g;
const trimUrl = (u) => u.replace(/[.,;:!?"']+$/, "");

const linkify = (text) =>
  String(text || "")
    .split(URL_RE)
    .map((part, i) =>
      i % 2 === 1 ? (
        <a
          key={`lnk-${i}`}
          href={trimUrl(part)}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="underline decoration-os-border underline-offset-2 hover:decoration-os-ink"
        >
          {trimUrl(part)}
        </a>
      ) : (
        part
      ),
    );

const fmtStamp = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const today = new Date();
  return d.toDateString() === today.toDateString()
    ? d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
    : d.toLocaleDateString([], { month: "short", day: "numeric" });
};

const TaskForm = ({ initial, busy, onSubmit, onCancel, submitLabel }) => {
  const [title, setTitle] = useState(initial?.title || "");
  const [desc, setDesc] = useState(initial?.description || "");
  const [link, setLink] = useState(initial?.project || "");
  const [err, setErr] = useState(null);
  // Project choices: the desk's own live feed of projects on the goals board —
  // any goal row can be the thing a task ties to. Free-typed overrides win.
  const choices = initial?.projectChoices || [];
  const linkable = choices.length > 0 && !initial?.project;
  return (
    <form
      className="flex flex-col gap-2 pt-1"
      onSubmit={(e) => {
        e.preventDefault();
        const t = title.trim();
        if (!t) { setErr("Give it a title — one line."); return; }
        setErr(null);
        onSubmit({ title: t, description: desc.trim(), project: link || null });
      }}
    >
      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Task title — one line"
        maxLength={120}
        disabled={busy}
        className="w-full rounded-control border border-os-border bg-white px-3 py-2 text-[0.875rem] text-os-ink placeholder:text-os-ink-4 focus:border-os-border-strong focus:outline-none"
      />
      <textarea
        value={desc}
        onChange={(e) => setDesc(e.target.value)}
        placeholder="Details — what exactly is done when this is done (optional, 3 lines max)"
        rows={2}
        maxLength={400}
        disabled={busy}
        className="w-full resize-none rounded-control border border-os-border bg-white px-3 py-2 text-[0.8125rem] leading-[1.45] text-os-ink placeholder:text-os-ink-4 focus:border-os-border-strong focus:outline-none"
      />
      {linkable && (
        <select
          value={link}
          onChange={(e) => setLink(e.target.value)}
          disabled={busy}
          className="w-full rounded-control border border-os-border bg-white px-3 py-2 text-[0.8125rem] text-os-ink-2 focus:border-os-border-strong focus:outline-none"
        >
          <option value="">Not tied to a project — just a one-off</option>
          {choices.map((c) => (
            <option key={c.slug} value={c.slug}>{c.title}</option>
          ))}
        </select>
      )}
      {initial?.project && (
        <p className="font-mono text-[0.5625rem] uppercase tracking-[0.08em] text-os-ink-3">
          tied to: {initial.project}
        </p>
      )}
      {err && <p className="text-[0.8125rem] text-os-red">{err}</p>}
      <div className="flex items-center justify-end gap-2">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded-control border border-os-border px-3 py-1.5 font-mono text-[0.6875rem] text-os-ink-3 hover:border-os-border-strong hover:text-os-ink disabled:opacity-40"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={busy}
          className="rounded-control bg-os-ink px-3 py-1.5 font-mono text-[0.6875rem] text-white hover:opacity-90 disabled:opacity-50"
        >
          {busy ? "Saving…" : submitLabel}
        </button>
      </div>
    </form>
  );
};

// ── Check-off composer (Jeff 2026-11-03: "I check it off and add a note") ───
// Checking off a task can carry a wrap-up note for Kit. For city-review tasks
// the sweep reads it: a note means "change this first" and holds outreach;
// a blank check-off releases outreach as-is.
const CheckOffComposer = ({ row, busy, onSubmit, onCancel }) => {
  const [draft, setDraft] = useState("");
  return (
    <div
      data-testid={`desk-task-notebox-${row.slug}`}
      onClick={(e) => e.stopPropagation()}
      className="mt-3 flex flex-col gap-2 rounded-control border border-os-border bg-white px-4 py-3"
    >
      <label
        htmlFor={`desk-task-note-${row.slug}`}
        className="font-mono text-[0.5625rem] uppercase tracking-[0.08em] text-os-ink-3"
      >
        Wrap-up note for Kit (optional)
      </label>
      <p className="text-[0.75rem] leading-[1.45] text-os-ink-2">
        Notes what to change and holds that work with Kit — leave empty to release things as-is.
      </p>
      <textarea
        id={`desk-task-note-${row.slug}`}
        data-testid={`desk-task-note-input-${row.slug}`}
        autoFocus
        rows={3}
        maxLength={400}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="e.g. Hero image is dated — swap it, then outreach can go."
        className="rounded-control border border-os-border px-3 py-2 text-[0.8125rem] text-os-ink placeholder:text-os-ink-4 focus:border-os-border-strong focus:outline-none"
      />
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          data-testid={`desk-task-note-submit-${row.slug}`}
          disabled={busy}
          onClick={() => onSubmit(row.slug, draft.trim())}
          className="rounded-control border border-os-ink bg-os-ink px-2.5 py-1 font-mono text-[0.6875rem] text-white disabled:opacity-40"
        >
          {draft.trim() ? "check off with note" : "check off"}
        </button>
        <button
          type="button"
          data-testid={`desk-task-note-cancel-${row.slug}`}
          disabled={busy}
          onClick={onCancel}
          className="rounded-control border border-os-border px-2.5 py-1 font-mono text-[0.6875rem] text-os-ink-3 hover:border-os-border-strong hover:text-os-ink disabled:opacity-40"
        >
          cancel
        </button>
      </div>
    </div>
  );
};

const TaskRowItem = ({ row, board, onFlash, expanded, onToggle, onEdit, editing, choices }) => {
  const p = row.payload || {};
  const done = row.state === "DONE";
  const busy = board.pending === row.slug;
  const [noteOpen, setNoteOpen] = useState(false);
  return (
    <div
      data-testid={`desk-task-${row.slug}`}
      className={cn(
        "border-t border-os-border first:border-t-0",
        editing && "bg-os-wash/30",
      )}
    >
      {editing ? (
        <div className="px-5 py-3">
          <TaskForm
            initial={{ ...p, projectChoices: choices }}
            busy={busy}
            submitLabel="Save task"
            onCancel={() => onEdit(null)}
            onSubmit={onEdit}
          />
        </div>
      ) : (
        <div
          role="button"
          tabIndex={0}
          aria-label={`${done ? "Task" : "Task, open"}: ${p.title} — expand to ${done ? "reopen or clear" : "edit, check off, or clear"}`}
          onClick={() => onToggle(row.slug)}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onToggle(row.slug))}
          className="flex cursor-pointer flex-wrap items-center gap-x-3 gap-y-1 px-5 py-3 text-left"
        >
          <button
            type="button"
            aria-label={done ? "Mark not done" : "Mark done"}
            title={done ? "Mark not done" : "Mark done"}
            disabled={busy}
            onClick={(e) => {
              e.stopPropagation();
              if (done) {
                board.act("taskReopen", row.slug).then((res) => {
                  if (res && onFlash) onFlash("Back open.");
                });
              } else {
                // Jeff 11-03: check-off opens the wrap-up composer — a blank
                // submit is still a plain check-off.
                setNoteOpen(true);
              }
            }}
            className={cn(
              "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-chip border",
              done ? "border-os-green bg-os-green text-white" : "border-os-border-strong bg-white hover:border-os-ink",
            )}
          >
            {done && (
              <svg viewBox="0 0 12 12" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M2.5 6.5L5 9l4.5-5.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </button>
          <span
            className={cn(
              "min-w-0 flex-1 truncate text-[0.875rem] font-medium",
              done ? "text-os-ink-3 line-through" : "text-os-ink",
            )}
          >
            {linkify(p.title)}
          </span>
          {p.project && (
            <span className="hidden shrink-0 font-mono text-[0.5625rem] uppercase tracking-[0.08em] text-os-ink-3 sm:inline" title="Tied to this project">
              ↔ {String(p.project).replace(/^(proj-|goal-)/, "").slice(0, 22)}
            </span>
          )}
          {expanded && <span className="font-mono text-[0.5625rem] uppercase tracking-[0.08em] text-os-ink-4">▾</span>}
        </div>
      )}
      {expanded && !editing && (
        <div
          onClick={(e) => e.stopPropagation()}
          className="border-t border-os-border bg-os-wash/40 px-5 pb-4 pt-3 motion-safe:transition-all motion-safe:duration-300"
        >
          <p className="whitespace-pre-line text-[0.8125rem] leading-[1.5] text-os-ink-2">
            {linkify(p.description || "No details — the title is the task.")}
          </p>
          {done && p.jeff?.taskDone?.note && (
            <p
              data-testid={`desk-task-sentnote-${row.slug}`}
              className="mt-2 border-l-2 border-os-amber/50 pl-3 whitespace-pre-line text-[0.8125rem] leading-[1.5] text-os-ink-2"
            >
              <span className="font-mono text-[0.5625rem] uppercase tracking-[0.08em] text-os-ink-3">
                Your note to Kit:{" "}
              </span>
              {p.jeff.taskDone.note}
            </p>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              data-testid={`desk-task-edit-${row.slug}`}
              disabled={busy}
              onClick={() => onEdit(row.slug, true)}
              className="rounded-control border border-os-border px-2.5 py-1 font-mono text-[0.6875rem] text-os-ink hover:border-os-border-strong disabled:opacity-40"
            >
              edit
            </button>
            <button
              type="button"
              data-testid={`desk-task-check-${row.slug}`}
              disabled={busy}
              onClick={() => {
                if (done) {
                  board.act("taskReopen", row.slug).then((res) => {
                    if (res && onFlash) onFlash("Back open.");
                  });
                } else {
                  setNoteOpen(true);
                }
              }}
              className="rounded-control border border-os-border px-2.5 py-1 font-mono text-[0.6875rem] text-os-ink hover:border-os-border-strong disabled:opacity-40"
            >
              {done ? "reopen" : "check off"}
            </button>
            {noteOpen && (
              <CheckOffComposer
                row={row}
                busy={busy}
                onSubmit={(slug, note) => {
                  board
                    .act("taskDone", slug, note || null)
                    .then((res) => {
                      if (!res) return;
                      setNoteOpen(false);
                      if (onFlash) {
                        onFlash(note ? "Done — note is with Kit." : "Done — checked off.");
                      }
                    });
                }}
                onCancel={() => setNoteOpen(false)}
              />
            )}
            {done && (
              <button
                type="button"
                data-testid={`desk-task-clear-${row.slug}`}
                disabled={busy}
                onClick={() =>
                  board.edit("taskClear", row.slug, {}).then((res) => {
                    if (res && onFlash) onFlash("Cleared — off the list.");
                  })
                }
                className="rounded-control border border-os-border px-2.5 py-1 font-mono text-[0.6875rem] text-os-ink-3 hover:border-os-border-strong hover:text-os-ink disabled:opacity-40"
              >
                clear
              </button>
            )}
            <span className="ml-auto font-mono text-[0.5625rem] text-os-ink-4">
              added {fmtStamp(row.updatedAt)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export const DeskTasks = ({ goalRows, board, onFlash }) => {
  const tasks = useMemo(
    () =>
      goalRows
        .filter((r) => r.kind === "task")
        .sort((a, b) => {
          if ((a.state === "DONE") !== (b.state === "DONE")) return a.state === "DONE" ? 1 : -1;
          return (b.updatedAt || "").localeCompare(a.updatedAt || "");
        }),
    [goalRows],
  );
  const openTasks = tasks.filter((t) => t.state !== "DONE");
  const doneTasks = tasks.filter((t) => t.state === "DONE");

  const [expandedSlug, setExpandedSlug] = useState(null);
  const [editSlug, setEditSlug] = useState(null);
  const [adding, setAdding] = useState(false);
  const [clearing, setClearing] = useState(false);

  // Project choices for the link picker: live project rows (by title), newest first.
  const choices = useMemo(
    () =>
      goalRows
        .filter((r) => r.kind === "goal" && ["EXECUTE", "PLAN", "TEST"].includes(r.state))
        .slice(0, 12)
        .map((r) => ({ slug: r.slug, title: (r.payload || {}).title || r.slug })),
    [goalRows],
  );

  const submitNew = useCallback(
    async ({ title, description, project }) => {
      const res = await board.edit("taskAdd", "", {
        ...(title ? { value: title } : {}),
        ...(description ? { note: description } : {}),
        ...(project ? { link: project } : {}),
      });
      if (res) {
        setAdding(false);
        onFlash?.("On the list — it's yours now, not Kit's.");
      }
      return res;
    },
    [board, onFlash],
  );

  const submitEdit = useCallback(
    async ({ title, description, project }) => {
      if (!editSlug) return null;
      const res = await board.edit("taskEdit", editSlug, {
        ...(title ? { value: title } : {}),
        note: description || "",
      });
      if (res) {
        setEditSlug(null);
        onFlash?.("Updated.");
      }
      return res;
    },
    [board, editSlug, onFlash],
  );

  return (
    <div className="flex h-full flex-col gap-2" data-testid="desk-tasks">
      <div className="flex flex-wrap items-baseline justify-between gap-2 px-1">
        <div className="flex items-center gap-2">
          <Label as="h2">Your tasks</Label>
          {openTasks.length > 0 && (
            <span
              data-testid="desk-tasks-open-tag"
              className="rounded-chip border border-os-amber/40 bg-os-amber/10 px-2 py-0.5 font-mono text-[0.5625rem] uppercase tracking-[0.08em] text-os-amber"
            >
              {openTasks.length} open
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {doneTasks.length > 0 && (
            <button
              type="button"
              data-testid="desk-tasks-clear-done"
              disabled={clearing}
              onClick={async () => {
                setClearing(true);
                const slugs = doneTasks.map((t) => t.slug);
                for (const s of slugs) {
                  await board.edit("taskClear", s, {});
                }
                setClearing(false);
                onFlash?.(`Cleared ${slugs.length} done task${slugs.length === 1 ? "" : "s"}.`);
              }}
              className="font-mono text-[0.6875rem] text-os-ink-3 underline-offset-4 hover:text-os-ink hover:underline disabled:opacity-40"
            >
              clear done ({doneTasks.length})
            </button>
          )}
          <button
            type="button"
            data-testid="desk-tasks-add"
            onClick={() => {
              setAdding((v) => !v);
              setEditSlug(null);
            }}
            className="rounded-control border border-os-border px-2.5 py-1 font-mono text-[0.6875rem] text-os-ink hover:border-os-border-strong"
          >
            {adding ? "close" : "+ task"}
          </button>
        </div>
      </div>

      <Card className="px-0 py-0">
        {adding && (
          <div className="border-b border-os-border bg-os-wash/30 px-5 py-3" data-testid="desk-task-new-form">
            <TaskForm
              busy={board.pending === "__new__"}
              submitLabel="Add task"
              onCancel={() => setAdding(false)}
              onSubmit={(fields) => {
                // board.edit targets a slug; a mint uses pending on the slug it returns.
                submitNew(fields);
              }}
            />
          </div>
        )}
        {tasks.length === 0 && !adding ? (
          <p className="px-5 py-4 text-[0.875rem] text-os-ink-2">
            Nothing on your list — add the small, specific things only you can do.
          </p>
        ) : (
          tasks.map((row) => (
            <TaskRowItem
              key={row.slug}
              row={row}
              board={board}
              onFlash={onFlash}
              expanded={expandedSlug === row.slug}
              onToggle={(slug) => setExpandedSlug((prev) => (prev === slug ? null : slug))}
              editing={editSlug === row.slug}
              onEdit={(slugOrFields, _) => {
                if (typeof slugOrFields === "string" || slugOrFields === null) {
                  setEditSlug(slugOrFields || null);
                  setAdding(false);
                } else {
                  submitEdit(slugOrFields);
                }
              }}
              choices={choices}
            />
          ))
        )}
      </Card>
      <MonoMeta as="p" className="px-1">
        Small one-off work — yours, not the executor's. Tasks stay until you clear them.
      </MonoMeta>
    </div>
  );
};

export default DeskTasks;